export const EXTERNAL_LINKS_ASSET_KEYS = Object.freeze({
  GITHUB_BANNER: 'GITHUB_BANNER',
  LEARN_MORE_BACKGROUND: 'LEARN_MORE_BACKGROUND',
  YOUTUBE_BUTTON: 'YOUTUBE_BUTTON',
  YOUTUBE_THUMB_NAIL: 'YOUTUBE_THUMB_NAIL',
});

export const BATTLE_BACKGROUND_ASSET_KEYS = Object.freeze({
  FOREST: 'FOREST',
  CAVE: 'CAVE',
  TOWN: 'TOWN',
});

// Overworld character skins. The player + NPCs are all drawn from the MPWSP01 "ow*" walk
// sheets (128x128, a 4col x 4row 32px grid: rows = DOWN / LEFT / RIGHT / UP). PLAYER_SKIN_KEYS
// is the ordered list the starter-select skin picker and the player/remote-player renderers
// use. See preload-scene.js for the load + animation wiring.
export const PLAYER_SKIN_KEYS = Object.freeze([
  'PLAYER_SKIN_1',
  'PLAYER_SKIN_2',
  'PLAYER_SKIN_3',
  'PLAYER_SKIN_4',
  'PLAYER_SKIN_5',
  'PLAYER_SKIN_6',
  'PLAYER_SKIN_7',
  'PLAYER_SKIN_8',
  'PLAYER_SKIN_9',
  'PLAYER_SKIN_10',
]);

// Original Monster-Hunter-style beasts (NOT Pokémon IP).
//
// Two sprite families now back the roster:
//   1. The 5 legacy monster-tamer placeholders (CINDERTAIL…RIMEHOWL, 256x256) — still used
//      for elements without dedicated art (stone / gale / venom / frost / beast / apex).
//   2. The real Catchimon battler art (the CATCHIMON_* keys, 80x80 pixel-art creatures) —
//      used for the flame / tide / verdant lines so those beasts have genuine, distinct
//      Pokémon-style sprites. See CONTENT_MAPPING.md for the full 24-beast mapping and
//      catchimon/LICENSE_*.txt for the (free) asset licenses.
export const MONSTER_ASSET_KEYS = Object.freeze({
  CINDERTAIL: 'CINDERTAIL',
  GLOOMFANG: 'GLOOMFANG',
  VOLTCRAG: 'VOLTCRAG',
  TIDEMAW: 'TIDEMAW',
  RIMEHOWL: 'RIMEHOWL',
  // Real Catchimon battlers (80x80) — distinct creatures wired by element.
  CATCHIMON_FIRE_01: 'CATCHIMON_FIRE_01',
  CATCHIMON_FIRE_02: 'CATCHIMON_FIRE_02',
  CATCHIMON_WATER_11: 'CATCHIMON_WATER_11',
  CATCHIMON_WATER_12: 'CATCHIMON_WATER_12',
  CATCHIMON_WATER_13: 'CATCHIMON_WATER_13',
  CATCHIMON_GRASS_19: 'CATCHIMON_GRASS_19',
  CATCHIMON_GRASS_20: 'CATCHIMON_GRASS_20',
  // Ultra-rare deep-Cave beasts (custom art, background removed).
  RARE_VOLTBLOOM: 'RARE_VOLTBLOOM',
  RARE_NYXWING: 'RARE_NYXWING',
  RARE_VOLTFOX: 'RARE_VOLTFOX',
  RARE_CRYOLITH: 'RARE_CRYOLITH',
  RARE_FERROK: 'RARE_FERROK',
  // New Monstara creatures (80x80, transparent) — replace duplicated placeholder battlers by theme.
  NM_FIREFOX: 'NM_FIREFOX',
  NM_VOLTBEAST: 'NM_VOLTBEAST',
  NM_STEELGOLEM: 'NM_STEELGOLEM',
  NM_BATDRAGON: 'NM_BATDRAGON',
  NM_ICEDRAGON: 'NM_ICEDRAGON',
  NM_CRYSTALDRAGON: 'NM_CRYSTALDRAGON',
  NM_CRYSTALFLYER: 'NM_CRYSTALFLYER',
  NM_GREENDINO: 'NM_GREENDINO',
  NM_DOLPHIN: 'NM_DOLPHIN',
  NM_FINLIZARD: 'NM_FINLIZARD',
  // Mythical venom line (community art): Solani → Solanoid.
  NM_SOLANI: 'NM_SOLANI',
  NM_SOLANOID: 'NM_SOLANOID',
});

export const BATTLE_ASSET_KEYS = Object.freeze({
  HEALTH_BAR_BACKGROUND: 'HEALTH_BAR_BACKGROUND',
  BALL_THUMBNAIL: 'BALL_THUMBNAIL',
  DAMAGED_BALL: 'DAMAGED_BALL',
  TRAINER_YOUTH_GIRL: 'TRAINER_YOUTH_GIRL',
  TRAINER_YOUTH_BOY: 'TRAINER_YOUTH_BOY',
});

export const HEALTH_BAR_ASSET_KEYS = Object.freeze({
  LEFT_CAP: 'LEFT_CAP',
  MIDDLE: 'MIDDLE',
  RIGHT_CAP: 'RIGHT_CAP',
  LEFT_CAP_SHADOW: 'LEFT_CAP_SHADOW',
  MIDDLE_SHADOW: 'MIDDLE_SHADOW',
  RIGHT_CAP_SHADOW: 'RIGHT_CAP_SHADOW',
});

export const EXP_BAR_ASSET_KEYS = Object.freeze({
  EXP_LEFT_CAP: 'EXP_LEFT_CAP',
  EXP_MIDDLE: 'EXP_MIDDLE',
  EXP_RIGHT_CAP: 'EXP_RIGHT_CAP',
});

export const UI_ASSET_KEYS = Object.freeze({
  CURSOR: 'CURSOR',
  CURSOR_WHITE: 'CURSOR_WHITE',
  MENU_BACKGROUND: 'MENU_BACKGROUND',
  MENU_BACKGROUND_GREEN: 'MENU_BACKGROUND_GREEN',
  MENU_BACKGROUND_PURPLE: 'MENU_BACKGROUND_PURPLE',
  BLUE_BUTTON: 'BLUE_BUTTON',
  BLUE_BUTTON_SELECTED: 'BLUE_BUTTON_SELECTED',
});

export const DATA_ASSET_KEYS = Object.freeze({
  ATTACKS: 'ATTACKS',
  ANIMATIONS: 'ANIMATIONS',
  ITEMS: 'ITEMS',
  MONSTERS: 'MONSTERS',
  ENCOUNTERS: 'ENCOUNTERS',
  NPCS: 'NPCS',
  EVENTS: 'EVENTS',
  SIGNS: 'SIGNS',
});

export const ATTACK_ASSET_KEYS = Object.freeze({
  ICE_SHARD: 'ICE_SHARD',
  ICE_SHARD_START: 'ICE_SHARD_START',
  SLASH: 'SLASH',
});

export const WORLD_ASSET_KEYS = Object.freeze({
  MAIN_1_BACKGROUND: 'MAIN_1_BACKGROUND',
  MAIN_1_FOREGROUND: 'MAIN_1_FOREGROUND',
  MAIN_1_LEVEL: 'MAIN_1_LEVEL',
  WORLD_COLLISION: 'WORLD_COLLISION',
  WORLD_ENCOUNTER_ZONE: 'WORLD_ENCOUNTER_ZONE',
  BEACH: 'BEACH',
  GRASS: 'GRASS',
  FOREST_1_BACKGROUND: 'FOREST_1_BACKGROUND',
  FOREST_1_FOREGROUND: 'FOREST_1_FOREGROUND',
  FOREST_1_LEVEL: 'FOREST_1_LEVEL',
  // Generated open-world overworld + cave/tunnel (see scripts/gen-world.mjs).
  OVERWORLD_1_BACKGROUND: 'OVERWORLD_1_BACKGROUND',
  OVERWORLD_1_FOREGROUND: 'OVERWORLD_1_FOREGROUND',
  OVERWORLD_1_LEVEL: 'OVERWORLD_1_LEVEL',
  CAVE_1_BACKGROUND: 'CAVE_1_BACKGROUND',
  CAVE_1_FOREGROUND: 'CAVE_1_FOREGROUND',
  CAVE_1_LEVEL: 'CAVE_1_LEVEL',
});

// Devil's Work.shop low-poly RPG prop atlas (top-down): 68 transparent props
// (barrels, chests, torches, flags, benches, tables, pillars, walls, eggs, gems, …)
// for world decor + interactive objects. Frame names = `<prop>.png` (see props-top.json).
export const LOWPOLY_ASSET_KEYS = Object.freeze({
  PROPS: 'LOWPOLY_PROPS',
});

export const BUILDING_ASSET_KEYS = Object.freeze({
  BUILDING_1_BACKGROUND: 'BUILDING_1_BACKGROUND',
  BUILDING_1_FOREGROUND: 'BUILDING_1_FOREGROUND',
  BUILDING_1_LEVEL: 'BUILDING_1_LEVEL',
  BUILDING_2_BACKGROUND: 'BUILDING_2_BACKGROUND',
  BUILDING_2_FOREGROUND: 'BUILDING_2_FOREGROUND',
  BUILDING_2_LEVEL: 'BUILDING_2_LEVEL',
  BUILDING_3_BACKGROUND: 'BUILDING_3_BACKGROUND',
  BUILDING_3_FOREGROUND: 'BUILDING_3_FOREGROUND',
  BUILDING_3_LEVEL: 'BUILDING_3_LEVEL',
});

export const CHARACTER_ASSET_KEYS = Object.freeze({
  PLAYER: 'PLAYER',
  NPC: 'NPC',
});

export const TITLE_ASSET_KEYS = Object.freeze({
  BACKGROUND: 'BACKGROUND',
  TITLE: 'TITLE',
  PANEL: 'PANEL',
});

export const MONSTER_PARTY_ASSET_KEYS = Object.freeze({
  PARTY_BACKGROUND: 'PARTY_BACKGROUND',
  MONSTER_DETAILS_BACKGROUND: 'MONSTER_DETAILS_BACKGROUND',
});

export const INVENTORY_ASSET_KEYS = Object.freeze({
  INVENTORY_BACKGROUND: 'INVENTORY_BACKGROUND',
  INVENTORY_BAG: 'INVENTORY_BAG',
});

export const AUDIO_ASSET_KEYS = Object.freeze({
  TITLE: 'TITLE',
  BATTLE: 'BATTLE',
  MAIN: 'MAIN',
  FLEE: 'FLEE',
  GRASS: 'GRASS',
  CLAW: 'CLAW',
  ICE: 'ICE',
  // Walking footsteps (surface-aware): sandy ground vs grass field. Played quietly.
  STEP_SAND: 'STEP_SAND',
  STEP_GRASS: 'STEP_GRASS',
  GAME_OVER: 'GAME_OVER', // deep "game over" voice on a battle defeat
  COIN: 'COIN', // single retro coin pickup (trimmed from a 4-coin clip)
});
